import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Card,
  Container,
  Divider,
  Group,
  Modal,
  Paper,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  Tooltip,
  Popover,
  ColorPicker,
} from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconBold,
  IconItalic,
  IconUnderline,
  IconList,
  IconListNumbers,
  IconQuote,
  IconLink,
  IconPhoto,
  IconAlignLeft,
  IconAlignCenter,
  IconAlignRight,
  IconAlignJustified,
  IconPalette,
  IconHighlight,
  IconSettings,
  IconEye,
  IconDeviceFloppy,
  IconArrowLeft,
  IconMinus,
} from "@tabler/icons-react";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";

import { Endpoints } from "../../../Lib/endpoints";
import { usePerformRequest } from "../../../Lib/usePerformRequest";
import { BlogProps } from "../../../Lib/types";
import { NonPaginatedResponse } from "../../../Lib/responses";
import { PerformRequest, UploadFiles } from "../../../Lib/usePerformRequest";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
} from "../../../Lib/methods";

import "./styles.scss";
import { HttpSuccessCodesObj } from "../../../Lib/data";

const SingleBlogEdit: React.FC = () => {
  const { blogID } = useParams();

  const {
    data: blog,
    isLoading: isLoadingBlog,
    reloadData: reloadBlog,
  } = usePerformRequest<NonPaginatedResponse<BlogProps>>({
    method: Endpoints.GetSingleBlogDetails(blogID as string).method,
    route: Endpoints.GetSingleBlogDetails(blogID as string).route,
  });

  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [isModalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);
  const [isUpdatingBlog, setIsUpdatingBlog] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [colorPopoverOpened, setColorPopoverOpened] = useState(false);
  const [selectedColor, setSelectedColor] = useState<string | undefined>(
    undefined
  );

  const blogForm = useForm<Partial<BlogProps>>({
    initialValues: {
      title: "",
      description: "",
      thumbnail: "",
      tags: [],
    },
    validate: {
      title: (v) => (v && v.length >= 3 ? null : "Title is too short"),
      description: (v) =>
        v && v.length >= 5 ? null : "Description is too short",
      tags: (v) => (v && v.length >= 1 ? null : "At least one tag is required"),
      thumbnail: (v) =>
        !v && v?.length === 0 && !thumbnail ? "Thumbnail is required" : null,
    },
  });

  const handleImageUpload = async (file: File) => {
    setIsUploadingImage(true);

    try {
      const uploaded = await UploadFiles([file as any]);

      if (uploaded && uploaded.status === HttpSuccessCodesObj.Created) {
        const imageUrl = uploaded.data[0].path;
        editor?.chain().focus().setImage({ src: imageUrl }).run();
        DefaultSuccessNotification("Image uploaded successfully!");
      } else {
        DefaultErrorNotification("Could not upload image!");
      }
    } catch (error) {
      DefaultErrorNotification("Could not upload image!");
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
    // Reset the input
    if (event.target) {
      event.target.value = "";
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      Link,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
    ],
    content: blog?.data?.content || "",
    onUpdate: ({ editor }) => {
      // Handle content updates
      console.log(editor.getHTML());
    },
  });

  useEffect(() => {
    if (blog?.data) {
      blogForm.setValues({
        title: blog.data.title,
        description: blog.data.description,
        thumbnail: blog.data.thumbnail,
        tags: blog.data.tags,
      });
      if (editor && blog.data.content) {
        editor.commands.setContent(blog.data.content);
      }
    }
  }, [blog, editor]);

  // Save blog content (editor) separately
  const UpdateBlogContent = async () => {
    if (!editor) return;
    setIsUpdatingBlog(true);
    const content = editor.getHTML();
    const r = await PerformRequest({
      method: Endpoints.GetUpdateBlogContent(blogID as string).method,
      route: Endpoints.GetUpdateBlogContent(blogID as string).route,
      data: {
        content,
      },
    });
    if (r) {
      if (r.status === 200) {
        setIsUpdatingBlog(false);
        DefaultSuccessNotification("Blog content updated successfully!");
        reloadBlog();
      } else {
        setIsUpdatingBlog(false);
        DefaultErrorNotification(r.message ?? "Could not update blog content!");
      }
    }
  };

  // Save blog details (modal)
  const UpdateBlogDetails = async () => {
    const { hasErrors } = blogForm.validate();
    if (hasErrors) return;
    setIsUpdatingBlog(true);
    const { title, description, tags } = blogForm.values;
    const updateBlogAction = async (thumbnailPath?: string) => {
      const r = await PerformRequest({
        method: Endpoints.GetUpdateBlog(blogID as string).method,
        route: Endpoints.GetUpdateBlog(blogID as string).route,
        data: {
          title,
          description,
          tags,
          ...(thumbnailPath && { thumbnail: thumbnailPath }),
        },
      });
      if (r) {
        if (r.status === 200) {
          setIsUpdatingBlog(false);
          DefaultSuccessNotification("Blog details updated successfully!");
          closeModal();
          reloadBlog();
        } else {
          setIsUpdatingBlog(false);
          DefaultErrorNotification(
            r.message ?? "Could not update blog details!"
          );
        }
      }
    };
    if (thumbnail) {
      UploadFiles([thumbnail as any])
        .then((uploaded) => {
          if (uploaded && uploaded.status === 201) {
            updateBlogAction(uploaded.data[0].path);
          } else {
            setIsUpdatingBlog(false);
            DefaultErrorNotification("Could not upload image!");
          }
        })
        .catch(() => {
          setIsUpdatingBlog(false);
          DefaultErrorNotification("Could not upload image!");
        });
    } else {
      updateBlogAction();
    }
  };

  const CancelModal = () => {
    closeModal();
    blogForm.reset();
    setThumbnail(null);
  };

  const blogFormInputProps = {
    disabled: isUpdatingBlog,
    loading: isUpdatingBlog,
    withAsterisk: true,
  };

  if (isLoadingBlog) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f8f9fa",
        }}
      >
        <div>Loading blog...</div>
      </div>
    );
  }

  if (!blog?.data) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f8f9fa",
        }}
      >
        <div>Blog not found</div>
      </div>
    );
  }

  return (
    <div
      className="blog-edit-page"
      style={{
        minHeight: "100vh",
        background: "#f8f9fa",
        padding: "20px",
        width: "100%",
      }}
    >
      {/* Header */}
      <Paper
        shadow="sm"
        p="lg"
        mb="lg"
        radius={8}
        className="header-paper"
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
        }}
      >
        <Group justify="space-between" align="center">
          <Group gap="md">
            <Button
              variant="white"
              color="dark"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => window.history.back()}
              radius={6}
              size="sm"
            >
              Back
            </Button>
            <div>
              <Text
                fw={700}
                fz={22}
                className="header-title"
                style={{ color: "white" }}
              >
                Edit Blog
              </Text>
              <Text
                fz={14}
                className="header-subtitle"
                style={{ color: "rgba(255, 255, 255, 0.8)" }}
              >
                {blog.data.title}
              </Text>
            </div>
          </Group>
          <Group gap="sm">
            <Button
              variant="white"
              color="dark"
              leftSection={<IconSettings size={16} />}
              radius={6}
              size="sm"
              onClick={openModal}
            >
              Blog Details
            </Button>
            <Button
              color="green"
              leftSection={<IconDeviceFloppy size={16} />}
              radius={6}
              loading={isUpdatingBlog}
              onClick={UpdateBlogContent}
              size="sm"
            >
              Save Changes
            </Button>
          </Group>
        </Group>
      </Paper>

      {/* Editor Container */}
      <div className="editor-container">
        {/* Editor Toolbar */}
        <Paper
          shadow="xs"
          p="md"
          radius={0}
          className="toolbar-paper"
          style={{
            background: "#f8fafc",
            borderBottom: "1px solid #e2e8f0",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
          }}
        >
          <Group gap="xs" wrap="nowrap">
            <Tooltip label="Bold">
              <ActionIcon
                variant={editor?.isActive("bold") ? "filled" : "subtle"}
                onClick={() => editor?.chain().focus().toggleBold().run()}
                radius={4}
                size="md"
              >
                <IconBold size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Italic">
              <ActionIcon
                variant={editor?.isActive("italic") ? "filled" : "subtle"}
                onClick={() => editor?.chain().focus().toggleItalic().run()}
                radius={4}
                size="md"
              >
                <IconItalic size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Underline">
              <ActionIcon
                variant={editor?.isActive("underline") ? "filled" : "subtle"}
                onClick={() => editor?.chain().focus().toggleUnderline().run()}
                radius={4}
                size="md"
              >
                <IconUnderline size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Highlight">
              <ActionIcon
                variant={editor?.isActive("highlight") ? "filled" : "subtle"}
                color={editor?.isActive("highlight") ? "yellow" : undefined}
                onClick={() => editor?.chain().focus().toggleHighlight().run()}
                radius={4}
                size="md"
              >
                <IconHighlight size={16} />
              </ActionIcon>
            </Tooltip>

            <Divider orientation="vertical" />

            <Tooltip label="Bullet List">
              <ActionIcon
                variant={editor?.isActive("bulletList") ? "filled" : "subtle"}
                onClick={() => editor?.chain().focus().toggleBulletList().run()}
                radius={4}
                size="md"
              >
                <IconList size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Numbered List">
              <ActionIcon
                variant={editor?.isActive("orderedList") ? "filled" : "subtle"}
                onClick={() =>
                  editor?.chain().focus().toggleOrderedList().run()
                }
                radius={4}
                size="md"
              >
                <IconListNumbers size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Blockquote">
              <ActionIcon
                variant={editor?.isActive("blockquote") ? "filled" : "subtle"}
                onClick={() => editor?.chain().focus().toggleBlockquote().run()}
                radius={4}
                size="md"
              >
                <IconQuote size={16} />
              </ActionIcon>
            </Tooltip>

            <Divider orientation="vertical" />

            <Tooltip label="Align Left">
              <ActionIcon
                variant={
                  editor?.isActive({ textAlign: "left" }) ? "filled" : "subtle"
                }
                onClick={() =>
                  editor?.chain().focus().setTextAlign("left").run()
                }
                radius={4}
                size="md"
              >
                <IconAlignLeft size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Align Center">
              <ActionIcon
                variant={
                  editor?.isActive({ textAlign: "center" })
                    ? "filled"
                    : "subtle"
                }
                onClick={() =>
                  editor?.chain().focus().setTextAlign("center").run()
                }
                radius={4}
                size="md"
              >
                <IconAlignCenter size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Align Right">
              <ActionIcon
                variant={
                  editor?.isActive({ textAlign: "right" }) ? "filled" : "subtle"
                }
                onClick={() =>
                  editor?.chain().focus().setTextAlign("right").run()
                }
                radius={4}
                size="md"
              >
                <IconAlignRight size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Justify">
              <ActionIcon
                variant={
                  editor?.isActive({ textAlign: "justify" })
                    ? "filled"
                    : "subtle"
                }
                onClick={() =>
                  editor?.chain().focus().setTextAlign("justify").run()
                }
                radius={4}
                size="md"
              >
                <IconAlignJustified size={16} />
              </ActionIcon>
            </Tooltip>

            <Divider orientation="vertical" />

            <Tooltip label="Add Link">
              <ActionIcon
                variant={editor?.isActive("link") ? "filled" : "subtle"}
                onClick={() => {
                  const url = window.prompt("Enter URL");
                  if (url) {
                    editor?.chain().focus().setLink({ href: url }).run();
                  }
                }}
                radius={4}
                size="md"
              >
                <IconLink size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Add Image">
              <ActionIcon
                variant="subtle"
                onClick={handleImageButtonClick}
                radius={4}
                size="md"
                loading={isUploadingImage}
                disabled={isUploadingImage}
              >
                <IconPhoto size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Insert Horizontal Rule">
              <ActionIcon
                variant="subtle"
                onClick={() =>
                  editor?.chain().focus().setHorizontalRule().run()
                }
                radius={4}
                size="md"
              >
                <IconMinus size={16} />
              </ActionIcon>
            </Tooltip>
            <Popover
              opened={colorPopoverOpened}
              onChange={setColorPopoverOpened}
              position="bottom"
              withArrow
              shadow="md"
            >
              <Popover.Target>
                <Tooltip label="Text Color">
                  <ActionIcon
                    variant={
                      editor?.isActive("textStyle") &&
                      editor?.getAttributes("textStyle").color
                        ? "filled"
                        : "subtle"
                    }
                    color={
                      editor?.getAttributes("textStyle").color || undefined
                    }
                    onClick={() => setColorPopoverOpened((o) => !o)}
                    radius={4}
                    size="md"
                    style={{
                      border: editor?.getAttributes("textStyle").color
                        ? `2px solid ${
                            editor?.getAttributes("textStyle").color
                          }`
                        : undefined,
                    }}
                  >
                    <IconPalette size={16} />
                  </ActionIcon>
                </Tooltip>
              </Popover.Target>
              <Popover.Dropdown>
                <ColorPicker
                  format="hex"
                  value={
                    selectedColor ||
                    editor?.getAttributes("textStyle").color ||
                    "#000000"
                  }
                  onChange={(color) => {
                    setSelectedColor(color);
                    editor?.chain().focus().setColor(color).run();
                  }}
                  swatches={[
                    "#000000",
                    "#fa5252",
                    "#fab005",
                    "#40c057",
                    "#228be6",
                    "#ae3ec9",
                    "#fff",
                  ]}
                  swatchesPerRow={7}
                  size="md"
                  withPicker={true}
                  fullWidth
                  style={{ width: 180 }}
                />
                <Button
                  mt={8}
                  size="xs"
                  variant="default"
                  fullWidth
                  onClick={() => {
                    setSelectedColor(undefined);
                    editor?.chain().focus().unsetColor().run();
                    setColorPopoverOpened(false);
                  }}
                >
                  Reset
                </Button>
              </Popover.Dropdown>
            </Popover>
          </Group>
        </Paper>

        {/* Editor Content */}
        <Paper
          shadow="xs"
          p="xl"
          radius={0}
          style={{
            background: "#fff",
            minHeight: "60vh",
            borderBottomLeftRadius: "8px",
            borderBottomRightRadius: "8px",
          }}
        >
          <EditorContent
            editor={editor}
            style={{
              minHeight: "55vh",
              outline: "none",
            }}
          />
        </Paper>
      </div>

      {/* Blog Settings Modal */}
      <Modal
        opened={isModalOpened}
        onClose={CancelModal}
        centered
        title={
          <Text fw={600} fz={18}>
            Blog Details
          </Text>
        }
        radius={8}
        padding="lg"
        size="lg"
        className="blog-settings-modal"
      >
        <TextInput
          label="Title"
          placeholder="Enter blog title"
          mb={12}
          {...blogFormInputProps}
          {...blogForm.getInputProps("title")}
        />

        <Textarea
          label="Short Description"
          placeholder="Enter a short description"
          mb={12}
          {...blogFormInputProps}
          {...blogForm.getInputProps("description")}
        />

        <TagsInput
          mb={12}
          label="Tags"
          placeholder="Enter tag and press Enter"
          {...blogFormInputProps}
          {...blogForm.getInputProps("tags")}
        />

        <Dropzone
          {...blogFormInputProps}
          style={{
            borderColor: blogForm.errors.thumbnail ? "red" : undefined,
          }}
          onDrop={(files) => {
            setThumbnail(files[0]);
            if (files && files[0]) {
              blogForm.setFieldError("thumbnail", null);
            }
          }}
          onReject={(files) => console.log("rejected files", files)}
          maxSize={5 * 1024 ** 2}
          accept={[MIME_TYPES.jpeg, MIME_TYPES.png]}
          maxFiles={1}
        >
          {thumbnail ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <img
                src={URL.createObjectURL(thumbnail)}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "200px",
                  borderRadius: "6px",
                }}
              />
            </div>
          ) : (
            <Group
              justify="center"
              gap="xl"
              mih={120}
              style={{ pointerEvents: "none" }}
            >
              <IconPhoto size={32} color="var(--mantine-color-dimmed)" />
              <div>
                <Text size="sm" inline>
                  Drag image here or click to select
                </Text>
                <Text size="xs" c="dimmed" inline mt={4}>
                  Select just ONE thumbnail. File should not exceed 5mb
                </Text>
              </div>
            </Group>
          )}
        </Dropzone>

        <Group justify="flex-end" mt={18}>
          <Button
            disabled={isUpdatingBlog}
            variant="default"
            onClick={CancelModal}
            radius={6}
          >
            Cancel
          </Button>
          <Button
            loading={isUpdatingBlog}
            color="blue"
            radius={6}
            onClick={UpdateBlogDetails}
          >
            Update Blog
          </Button>
        </Group>
      </Modal>

      {/* Hidden file input for image upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: "none" }}
      />
    </div>
  );
};

export default SingleBlogEdit;
